import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import type { Moment } from '@this/src/lib/moment';
import moment from '@this/src/lib/moment';
import SimpleLoading from '@this/shared/simple_loading/simple_loading';

import PurchaseLeadtimeTooltip from '@this/components/organization/dashboard/sections/purchase_leadtime_tooltip/purchase_leadtime_tooltip';
import { styled } from '@this/constants/themes';
import { sortBarPurchaseLeadtime } from '@this/components/organization/dashboard/share/graph';
import type { DashboardProps } from '../../share';
import type { BarPurchaseLeadtime } from '../../share/purchase_leadtime_pile_up_graph';
import { chartOptions, getBarData, getBarHeight } from '../../share/purchase_leadtime_pile_up_graph';
import type { DashboardRequest } from '../../share/fetch';
import { fetchDashboard } from '../../share/fetch';
import { RefreshCacheButton } from '../../share/refresh_cache_button';

// 費用負担部署別セクションV2：グラフのスタイルテーマを作成して適用したもの
// Usecase::Organization::DashboardTrips::DashboardPurchaseLeadtimeByTravelerDepartmentSection::CachePatternUsecase を使用
const USECASE = { section: 'purchase_lead_time_by_traveler_department', pattern: 'cache' };

interface State {
  loading: boolean;
  leadtimes: BarPurchaseLeadtime[];
  lastUpdatedAt: Moment | null;
  refreshAt: Moment | null;
}

interface Response {
  leadtimes: State['leadtimes'];
  last_updated_at: string;
}

const fetchSummary = async ({ setState, ...props }: DashboardRequest<State>) => {
  setState(state => ({ ...state, loading: true }));
  const result = await fetchDashboard<Response>({ ...USECASE, ...props });
  setState(state => ({
    ...state,
    loading: false,
    leadtimes: result.leadtimes,
    lastUpdatedAt: result.last_updated_at ? moment(result.last_updated_at) : null
  }));
};

const DashboardPurchaseLeadtimeByTravelerDepartmentSection: React.FC<DashboardProps> = props => {
  const [state, setState] = useState<State>({
    loading: true,
    leadtimes: [],
    lastUpdatedAt: null,
    refreshAt: null
  });
  const { loading, leadtimes, lastUpdatedAt, refreshAt } = state;
  const { urlQuery, organizationId } = props;
  const { type, from, to } = urlQuery;

  const data = useMemo(() => getBarData(sortBarPurchaseLeadtime(leadtimes, 'desc'), 'default'), [leadtimes]);
  const height = useMemo(() => getBarHeight(leadtimes), [leadtimes]);

  const handleRefreshCache = useCallback(() => {
    setState(state => ({ ...state, refreshAt: moment() }));
  }, [setState]);

  useEffect(() => {
    fetchSummary({ setState, ...props });
  }, [type, from, to, refreshAt, props.refreshAt]);

  return (
    <>
      <Wrap className="graph_title">
        <span>購買リードタイム（所属部署別）</span>
        <PurchaseLeadtimeTooltip />
      </Wrap>
      {loading ? (
        <SimpleLoading />
      ) : (
        <Bar data={data} width={550} height={height} options={{ indexAxis: 'y', ...chartOptions }} />
      )}
      <RefreshCacheButton
        cacheType={USECASE.section}
        dateType={type}
        from={from}
        to={to}
        organizationId={organizationId}
        lastUpdatedAt={lastUpdatedAt}
        onRefreshCache={handleRefreshCache}
      />
    </>
  );
};

const Wrap = styled.h3`
  display: flex;
  align-items: flex-start;
  position: relative;
`;

export default DashboardPurchaseLeadtimeByTravelerDepartmentSection;
